import * as React from 'react';
import {useFormContext} from 'react-hook-form';
import { Controller } from "react-hook-form"

import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import {Select as MuiSelect} from '@mui/material';
import {TextField as MuiTextField} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

const generateRandomHex = (length) => {
    const randomNumber = Math.random().toString(16);
    return randomNumber.substr(2, length);
}

const FormDropDownFilter = ({
    name,
    label,
    list,
    required,
    rules = {},
    menuItemProps = {},
    fullWidth,
    enableTextFilter = false,
    enableSelectFilter = false,
    selectFilterList = [],
    defaultTextFilter = '',
    defaultSelectFilter = '',
    ...props
}) => {

    const {control, formState } = useFormContext();

    const errors = formState.errors;
    const hasError = (name in errors) ? true : false;
    const errorMessage = (name in errors) ? errors[name]?.message : '';

    const requiredDefaultRule = required ? { required: { value: true, message: "This field is required" } } : {};
    rules = { ...requiredDefaultRule, ...rules };

    fullWidth = (fullWidth === undefined) ? true : fullWidth;

    const id = React.useRef(generateRandomHex(8)).current;
    const labelId = id + "-label";
    const selectFilterLabelId = id + "-select-filter-label";

    const [filterValue, setFilterValue] = React.useState(defaultTextFilter);
    const [selectFilterValue, setSelectFilterValue] = React.useState(defaultSelectFilter);
    const [filtersOpen, setFiltersOpen] = React.useState(false);

    const changeFilter = (e) => {
        setFilterValue(e.target.value);
    }

    const changeSelectFilter = (e) => {
        setSelectFilterValue(e.target.value);
    }

    const resetFilters = () => {
        setFilterValue('');
        setSelectFilterValue('');
    }

    const hasFilters = enableTextFilter || enableSelectFilter;

    const filteredList = React.useMemo(() => {
        let result = list;
        if (enableTextFilter && filterValue.trim()) {
            result = result.filter((cur) =>
                String(cur.label || '').toLowerCase().includes(filterValue.toLowerCase())
            );
        }
        if (enableSelectFilter && selectFilterValue) {
            result = result.filter((cur) =>
                String(cur.label || '').toLowerCase().includes(String(selectFilterValue).toLowerCase())
            );
        }
        return result;
    }, [list, enableTextFilter, filterValue, enableSelectFilter, selectFilterValue]);

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Controller
                    render={({field: {onChange, value, onBlur}}) =>(
                        <FormControl fullWidth={fullWidth} error={(hasError) ? true : false}>
                            <InputLabel id={labelId} required={required}>{label}</InputLabel>
                            <MuiSelect
                                onBlur={onBlur}
                                labelId={labelId}
                                label={label}
                                required={required}
                                value={value}
                                onChange={onChange}
                                {...props}
                            >
                                {filteredList.map((cur)=>(<MenuItem key={cur.value || cur.label} value={cur.value || cur.label} {...menuItemProps}>{cur.label}</MenuItem>))}
                            </MuiSelect>
                            {hasError && <FormHelperText>{String(errorMessage)}</FormHelperText>}
                        </FormControl>
                    )}
                    name={name}
                    control={control}
                    rules={rules}
                />
                {hasFilters && (
                    <IconButton onClick={() => setFiltersOpen((prev) => !prev)} aria-label="Toggle filters">
                        <FilterListIcon />
                    </IconButton>
                )}
            </Stack>
            {hasFilters && (
                <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        {enableTextFilter && (
                            <MuiTextField
                                label={label + " Free Filter"}
                                value={filterValue}
                                onChange={changeFilter}
                                fullWidth
                                size="small"
                            />
                        )}
                        {enableSelectFilter && (
                            <FormControl fullWidth size="small">
                                <InputLabel id={selectFilterLabelId}>{label + " Select Filter"}</InputLabel>
                                <MuiSelect
                                    labelId={selectFilterLabelId}
                                    label={label}
                                    value={selectFilterValue}
                                    onChange={changeSelectFilter}
                                >
                                    <MenuItem value=""><em>All</em></MenuItem>
                                    {selectFilterList.map((cur) => (
                                        <MenuItem key={cur.value || cur.label} value={cur.value}>{cur.label}</MenuItem>
                                    ))}
                                </MuiSelect>
                            </FormControl>
                        )}
                        <Button variant="outlined" size="small" onClick={resetFilters} sx={{ whiteSpace: 'nowrap' }}>Reset</Button>
                    </Stack>
                </Collapse>
            )}
        </Box>
    );
};

export default FormDropDownFilter;
